/**
 * NOC Calendar View
 * 
 * Centralized view for managing shift assignments and time-off/vacation schedules.
 * Allows NOC users to manage shifts and time-off for the team over a 2-week period.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from 'moment-timezone';

import CalendarWeekView from '../components/CalendarWeekView';
import { TIME_OFF_TYPES, TEAM_SHIFT_MAPPING } from '../config/shiftConfig';
import {
  useGetShiftAssignmentsByDateRangeQuery,
  useAddShiftAssignmentMutation,
  useUpdateShiftAssignmentMutation,
  useDeleteShiftAssignmentMutation,
  useGetShiftsQuery,
  useGetTeamsQuery,
  useLazyGetTeamQuery,
  useGetTimeOffQuery,
  useAddTimeOffMutation,
  useUpdateTimeOffMutation,
  useDeleteTimeOffMutation,
} from '../api/homeApi';
import { 
  getCurrentWeekRange, 
  formatDateToAPI,
  hasShiftEditPermission,
  isNOCUser,
  formatDateStringForDisplay
} from '../utils/shiftUtils';

const ShiftsManagement = ({ currentUser = {} }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [openVacationDialog, setOpenVacationDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingTimeOff, setEditingTimeOff] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeekRange());
  const [currentTime, setCurrentTime] = useState(new Date()); // For auto-refresh of current shift box

  // Update current week when selected date changes
  useEffect(() => {
    setCurrentWeek(getCurrentWeekRange(selectedDate));
  }, [selectedDate]);

  // Always get the week range for today (for the "On Duty Now" box)
  const todayWeek = useMemo(() => getCurrentWeekRange(new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTime intentionally used as an auto-refresh trigger, not read in the computation
    [currentTime]);

  const [shiftFormData, setShiftFormData] = useState({
    users: [],
    shift: '',
    team: '',
    notes: '',
    is_on_call: false,
    selectedDates: [],
  });
  const [vacationFormData, setVacationFormData] = useState({
    start_date: '',
    end_date: '',
    time_off_type: 'vacation',
    reason: '',
    user: '',
  });

  // Fetch data for selected week (shown in calendar and list)
  // Expand range to include prev and next week to ensure we always have both weeks visible when navigating
  const expandedWeekStart = useMemo(() => {
    return moment(currentWeek.start).subtract(7, 'days').toDate();
  }, [currentWeek.start]);

  const expandedWeekEnd = useMemo(() => {
    return moment(currentWeek.end).add(7, 'days').toDate();
  }, [currentWeek.end]);

  const {
    data: assignments = [],
    isLoading: assignmentsLoading,
    refetch: refetchAssignments,
  } = useGetShiftAssignmentsByDateRangeQuery({
    start_date: formatDateToAPI(expandedWeekStart),
    end_date: formatDateToAPI(expandedWeekEnd),
  });

  // Fetch data for today's week (for "On Duty Now" box - always current)
  const {
    data: todayAssignments = [],
  } = useGetShiftAssignmentsByDateRangeQuery({
    start_date: formatDateToAPI(todayWeek.start),
    end_date: formatDateToAPI(todayWeek.end),
  });

  const { data: shifts = [], isLoading: shiftsLoading } = useGetShiftsQuery();
  const { data: teams = [], isLoading: teamsLoading } = useGetTeamsQuery();

  // Get time-offs for the entire 2-week period
  const { 
    data: allTimeOffs = [], 
    isLoading: timeOffsLoading,
    refetch: refetchTimeOffs,
  } = useGetTimeOffQuery({});

  // Lazy query for team details
  const [getTeamDetails, { data: teamDetails }] = useLazyGetTeamQuery();

  // Shift Mutations
  const [addShiftAssignment, { isLoading: addLoading }] = useAddShiftAssignmentMutation();
  const [updateShiftAssignment, { isLoading: updateLoading }] = useUpdateShiftAssignmentMutation();
  const [deleteShiftAssignment] = useDeleteShiftAssignmentMutation();

  // Vacation Mutations
  const [addTimeOff, { isLoading: addVacationLoading }] = useAddTimeOffMutation();
  const [updateTimeOff, { isLoading: updateVacationLoading }] = useUpdateTimeOffMutation();
  const [deleteTimeOff] = useDeleteTimeOffMutation();

  // Check if current user has edit permissions for shifts (only specific users)
  const canEditShifts = hasShiftEditPermission(currentUser.username);

  // Check if current user can add vacation (NOC users or shift admins)
  const isNOC = isNOCUser(currentUser.groups);
  const canAddVacation = isNOC || canEditShifts;

  // Helper function to check if a time is within a shift range (handles wraparound for shifts crossing midnight)
  const isTimeInShift = (currentTime, startTime, endTime) => {
    if (startTime <= endTime) {
      // Normal case: shift doesn't cross midnight
      return currentTime >= startTime && currentTime < endTime;
    } else {
      // Wraparound case: shift crosses midnight (e.g., 3pm to midnight)
      return currentTime >= startTime || currentTime < endTime;
    }
  };

  // Get current shifts and next shifts organized by team/country
  const getCurrentAndNextShiftsByTeam = useMemo(() => {
    // Get current time in EST
    const nowEST = moment().tz('America/New_York');
    const currentTimeEST = nowEST.format('HH:mm:ss');
    const currentDateEST = nowEST.format('YYYY-MM-DD');

    // Use todayAssignments instead of assignments to always get today's data
    const todayAssignmentsData = todayAssignments.filter(
      a => a.assignment_date === currentDateEST
    );

    if (todayAssignmentsData.length === 0) {
      return {};
    }

    // Group by team
    const byTeam = {};
    todayAssignmentsData.forEach(assignment => {
      const teamName = assignment.team_detail?.name || 'Unknown';
      if (!byTeam[teamName]) {
        byTeam[teamName] = [];
      }
      byTeam[teamName].push(assignment);
    });

    // For each team, find current and next shifts
    const result = {};
    Object.keys(byTeam).forEach(teamName => {
      const teamShifts = byTeam[teamName];
      
      // Find current shifts (handles wraparound for shifts crossing midnight)
      const currentShifts = teamShifts.filter(assignment => {
        const startTime = assignment.shift_detail?.start_time || '00:00:00';
        const endTime = assignment.shift_detail?.end_time || '23:59:59';
        return isTimeInShift(currentTimeEST, startTime, endTime);
      });

      let nextShifts = [];

      if (currentShifts.length > 0) {
        // Find all shifts that start after the current time, excluding current shifts
        const upcomingShifts = teamShifts.filter(assignment => {
          const startTime = assignment.shift_detail?.start_time || '00:00:00';
          const isCurrentShift = currentShifts.find(cs => cs.id === assignment.id);
          return startTime > currentTimeEST && !isCurrentShift;
        });

        if (upcomingShifts.length > 0) {
          // Sort by start time and get all shifts with the earliest start time
          const sorted = upcomingShifts.sort((a, b) => {
            const timeA = a.shift_detail?.start_time || '00:00:00';
            const timeB = b.shift_detail?.start_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
          const earliestStartTime = sorted[0].shift_detail?.start_time;
          nextShifts = sorted.filter(s => s.shift_detail?.start_time === earliestStartTime);
        } else {
          // No upcoming shifts after current time - wrap to first shift
          const notCurrentShifts = teamShifts.filter(assignment => !currentShifts.find(cs => cs.id === assignment.id));
          const sorted = notCurrentShifts.sort((a, b) => {
            const timeA = a.shift_detail?.start_time || '00:00:00';
            const timeB = b.shift_detail?.start_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
          if (sorted.length > 0) {
            const earliestStartTime = sorted[0].shift_detail?.start_time;
            nextShifts = sorted.filter(s => s.shift_detail?.start_time === earliestStartTime);
          }
        }
      } else {
        // No current shifts - find the upcoming ones that start after now
        const upcomingShifts = teamShifts.filter(assignment => {
          const startTime = assignment.shift_detail?.start_time || '00:00:00';
          return startTime > currentTimeEST;
        });

        if (upcomingShifts.length > 0) {
          // Sort by start time and get all shifts with the earliest start time
          const sorted = upcomingShifts.sort((a, b) => {
            const timeA = a.shift_detail?.start_time || '00:00:00';
            const timeB = b.shift_detail?.start_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
          const earliestStartTime = sorted[0].shift_detail?.start_time;
          nextShifts = sorted.filter(s => s.shift_detail?.start_time === earliestStartTime);
        } else {
          // No upcoming shifts - wrap to first shift by start time
          const sorted = teamShifts.sort((a, b) => {
            const timeA = a.shift_detail?.start_time || '00:00:00';
            const timeB = b.shift_detail?.start_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
          if (sorted.length > 0) {
            const earliestStartTime = sorted[0].shift_detail?.start_time;
            nextShifts = sorted.filter(s => s.shift_detail?.start_time === earliestStartTime);
          }
        }
      }

      if (currentShifts.length > 0 || nextShifts.length > 0) {
        result[teamName] = { current: currentShifts, next: nextShifts };
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTime intentionally used as an auto-refresh trigger
  }, [todayAssignments, currentTime]);

  // Get today's time-offs only
  const getTodayTimeOffs = useMemo(() => {
    const todayString = moment().tz('America/New_York').format('YYYY-MM-DD');
    return allTimeOffs.filter(t => {
      const startDate = moment(t.start_date).format('YYYY-MM-DD');
      const endDate = moment(t.end_date).format('YYYY-MM-DD');
      return todayString >= startDate && todayString <= endDate;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTime intentionally used as an auto-refresh trigger
  }, [allTimeOffs, currentTime]);

  // Get all users from teams for admin selection in vacation dialog
  const adminUsers = useMemo(() => {
    if (!canEditShifts) return [];
    
    // Get unique users from all teams
    const usersMap = new Map();
    teams.forEach(team => {
      if (team.users && Array.isArray(team.users)) {
        team.users.forEach(user => {
          if (!usersMap.has(user.id)) {
            usersMap.set(user.id, user);
          }
        });
      }
    });
    return Array.from(usersMap.values());
  }, [teams, canEditShifts]);

  // Load team users when team is selected - only when dialog opens with a team
  useEffect(() => {
    if (shiftFormData.team && openShiftDialog) {
      getTeamDetails({ id: shiftFormData.team });
    }
  }, [openShiftDialog, shiftFormData.team, getTeamDetails]);

  // Update team users when team details are loaded
  useEffect(() => {
    if (teamDetails && teamDetails.users) {
      setTeamUsers(teamDetails.users);
    } else {
      setTeamUsers([]);
    }
  }, [teamDetails]);

  // Update currentWeek only when navigating with arrows, not when clicking on a date
  useEffect(() => {
    // Don't change currentWeek when selectedDate changes - only use it for calendar display
    // This keeps the 2-week view intact when clicking on different dates
  }, [selectedDate]);

  // Auto-refresh current time every 60 seconds to update the shift box
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(intervalId);
  }, []);

  // Filter time offs for the current 2-week period
  const periodTimeOffs = useMemo(() => {
    return allTimeOffs.filter(t => {
      const startDate = new Date(t.start_date);
      const endDate = new Date(t.end_date);
      const periodStart = currentWeek.start;
      const periodEnd = currentWeek.end;
      
      return (startDate <= periodEnd && endDate >= periodStart);
    });
  }, [allTimeOffs, currentWeek]);

  // Filter shifts based on selected team
  const filteredShifts = useMemo(() => {
    if (!shiftFormData.team) return shifts;
    
    const selectedTeam = teams.find(t => t.id === parseInt(shiftFormData.team));
    if (!selectedTeam) return shifts;
    
    const teamName = selectedTeam.name || '';
    const teamConfig = TEAM_SHIFT_MAPPING[teamName];
    
    if (!teamConfig || !teamConfig.prefix) {
      // If team config not found, try to match by looking at team name keywords
      let matchPrefix = '';
      if (teamName.toLowerCase().includes('usa') || teamName.toLowerCase().includes('us')) {
        matchPrefix = 'US';
      } else if (teamName.toLowerCase().includes('italy') || teamName.toLowerCase().includes('italian')) {
        matchPrefix = 'IT';
      } else if (teamName.toLowerCase().includes('india')) {
        matchPrefix = 'IN';
      }
      
      if (!matchPrefix) return shifts;
      
      return shifts.filter(shift => {
        const shiftName = (shift.name || '').toUpperCase();
        return shiftName.startsWith(matchPrefix);
      });
    }
    
    return shifts.filter(shift => {
      const shiftName = (shift.name || '').toUpperCase();
      const prefixMatch = shiftName.startsWith(teamConfig.prefix);
      const weekendMatch = teamConfig.includeWeekend && shiftName.includes('WEEKEND');
      return prefixMatch || weekendMatch;
    });
  }, [shiftFormData.team, shifts, teams]);

  // Group assignments by date
  const assignmentsByDate = useMemo(() => {
    return assignments.reduce((acc, assignment) => {
      const date = assignment.assignment_date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(assignment);
      return acc;
    }, {});
  }, [assignments]);

  // Get assignments for selected date
  const selectedDateAssignments = assignmentsByDate[formatDateToAPI(selectedDate)] || [];

  // Get available dates for shift assignment (current 2 weeks)
  const getAvailableDates = useMemo(() => {
    const dates = [];
    const start = moment(currentWeek.start).clone();
    const end = moment(currentWeek.end).clone().add(7, 'days'); // Include next week too
    
    while (start.isBefore(end)) {
      dates.push({
        date: start.clone(),
        formatted: start.format('ddd, MMM DD'),
        dateString: start.format('YYYY-MM-DD'),
        isToday: start.format('YYYY-MM-DD') === moment().tz('America/New_York').format('YYYY-MM-DD'),
      });
      start.add(1, 'day');
    }
    return dates;
  }, [currentWeek]);

  const handleOpenShiftDialog = (assignment = null) => {
    if (!canEditShifts) {
      alert('You do not have permission to edit shifts');
      return;
    }
    
    if (assignment) {
      setEditingAssignment(assignment);
      setShiftFormData({
        users: [assignment.user_detail?.username || assignment.user],
        shift: assignment.shift,
        team: assignment.team || '',
        notes: assignment.notes || '',
        is_on_call: assignment.is_on_call || false,
        selectedDates: [formatDateToAPI(selectedDate)],
      });
    } else {
      setEditingAssignment(null);
      setShiftFormData({
        users: [],
        shift: '',
        team: '',
        notes: '',
        is_on_call: false,
        selectedDates: [formatDateToAPI(selectedDate)],
      });
    }
    setOpenShiftDialog(true);
  };

  const handleCloseShiftDialog = () => {
    setOpenShiftDialog(false);
    setEditingAssignment(null);
    setTeamUsers([]);
  };

  const handleOpenVacationDialog = (timeOff = null) => {
    if (timeOff && timeOff.user_detail?.username !== currentUser.username && !canEditShifts) {
      alert('You can only edit your own vacation requests');
      return;
    }

    if (timeOff) {
      setEditingTimeOff(timeOff);
      setVacationFormData({
        start_date: timeOff.start_date,
        end_date: timeOff.end_date,
        time_off_type: timeOff.time_off_type,
        reason: timeOff.reason || '',
        user: timeOff.user_detail?.username || '',
      });
    } else {
      setEditingTimeOff(null);
      setVacationFormData({
        start_date: formatDateToAPI(selectedDate),
        end_date: formatDateToAPI(selectedDate),
        time_off_type: 'vacation',
        reason: '',
        user: currentUser.username || '',
      });
    }
    setOpenVacationDialog(true);
  };

  const handleCloseVacationDialog = () => {
    setOpenVacationDialog(false);
    setEditingTimeOff(null);
  };

  const handleShiftFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // If team is being changed, fetch team details immediately
    if (name === 'team' && value) {
      getTeamDetails({ id: value });
    }
    
    setShiftFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue,
      };
      
      // Reset user selection when team changes
      if (name === 'team') {
        updated.users = [];
        setTeamUsers([]);
      }
      
      return updated;
    });
  };

  const handleVacationFormChange = (e) => {
    const { name, value } = e.target;
    setVacationFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleShiftUsersChange = (e) => {
    const { value } = e.target;
    setShiftFormData(prev => ({
      ...prev,
      users: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleShiftDateChange = (dateString) => {
    setShiftFormData(prev => {
      const newDates = prev.selectedDates.includes(dateString)
        ? prev.selectedDates.filter(d => d !== dateString)
        : [...prev.selectedDates, dateString];
      return {
        ...prev,
        selectedDates: newDates,
      };
    });
  };

  const handleShiftSave = async (shouldClose = true) => {
    // Validate required fields
    if (!shiftFormData.shift) {
      alert('Please select a shift');
      return;
    }
    if (!shiftFormData.team) {
      alert('Please select a team');
      return;
    }
    if (!shiftFormData.users || shiftFormData.users.length === 0) {
      alert('Please select at least one user');
      return;
    }
    if (!shiftFormData.selectedDates || shiftFormData.selectedDates.length === 0) {
      alert('Please select at least one date');
      return;
    }

    try {
      // For editing single assignment
      if (editingAssignment) {
        const body = {
          user: shiftFormData.users[0],
          shift: parseInt(shiftFormData.shift),
          team: parseInt(shiftFormData.team),
          assignment_date: formatDateToAPI(selectedDate),
          notes: shiftFormData.notes,
          is_on_call: shiftFormData.is_on_call,
        };
        await updateShiftAssignment({ 
          id: editingAssignment.id, 
          body 
        }).unwrap();
      } else {
        // For creating multiple assignments across selected dates
        for (const dateString of shiftFormData.selectedDates) {
          for (const username of shiftFormData.users) {
            const body = {
              user: username,
              shift: parseInt(shiftFormData.shift),
              team: parseInt(shiftFormData.team),
              assignment_date: dateString,
              notes: shiftFormData.notes,
              is_on_call: shiftFormData.is_on_call,
            };
            await addShiftAssignment({ body }).unwrap();
          }
        }
      }

      refetchAssignments();
      
      if (shouldClose) {
        handleCloseShiftDialog();
      } else {
        // Clear the fields for next entry, but keep selected dates
        setShiftFormData({
          users: [],
          shift: '',
          team: shiftFormData.team, // Keep team selected
          notes: '',
          is_on_call: false,
          selectedDates: shiftFormData.selectedDates, // Keep dates selected
        });
      }
    } catch (error) {
      alert('Error saving assignment: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleVacationSave = async () => {
    // Validate dates
    if (!vacationFormData.start_date || !vacationFormData.end_date) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(vacationFormData.start_date) > new Date(vacationFormData.end_date)) {
      alert('End date must be after start date');
      return;
    }

    try {
      const body = {
        ...vacationFormData,
        user: vacationFormData.user || currentUser.username,
        approved: true,
      };

      if (editingTimeOff) {
        await updateTimeOff({ 
          id: editingTimeOff.id, 
          body 
        }).unwrap();
      } else {
        await addTimeOff({ body }).unwrap();
      }

      refetchTimeOffs();
      handleCloseVacationDialog();
    } catch (error) {
      alert('Error saving vacation: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleDeleteShift = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    try {
      await deleteShiftAssignment({ id: assignmentId }).unwrap();
      refetchAssignments();
    } catch (error) {
      alert('Error deleting assignment: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleDeleteVacation = async (timeOffId) => {
    if (!window.confirm('Are you sure you want to delete this vacation?')) return;

    try {
      await deleteTimeOff({ id: timeOffId }).unwrap();
      refetchTimeOffs();
    } catch (error) {
      alert('Error deleting vacation: ' + (error?.message || 'Unknown error'));
    }
  };

  if (assignmentsLoading || shiftsLoading || teamsLoading || timeOffsLoading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  // Format selected date for display
  const selectedDateFormatted = selectedDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          NOC Calendar
        </Typography>
      </Box>

      {/* Current and Next Shift Box */}
      {Object.keys(getCurrentAndNextShiftsByTeam).length > 0 ? (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #0a2240 0%, #1565C0 100%)', color: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  On Duty Now
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 'bold' }}>
                  EST: {moment().tz('America/New_York').format('HH:mm:ss')}
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              {/* Left column: Shift assignments by country */}
              <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
              {Object.keys(getCurrentAndNextShiftsByTeam).map(teamName => {
                const { current, next } = getCurrentAndNextShiftsByTeam[teamName];
                return (
                  <Grid item xs={12} sm={6} md={4} key={teamName}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Team Name Header */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', opacity: 0.9, mb: 0.5, fontSize: '0.85rem' }}>
                        🔵 {teamName}
                      </Typography>
                      
                      {/* Current Shift Row */}
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: current.length > 0 ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                          borderRadius: 1.5,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          minHeight: '80px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: current.length > 0 ? 'flex-start' : 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontWeight: 'bold', fontSize: '0.7rem' }}>
                          On Shift Now
                        </Typography>
                        {current.length > 0 ? (
                          current.map((assignment, idx) => (
                            <Box key={assignment.id} sx={{ mb: idx < current.length - 1 ? 1 : 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                {assignment.user_detail?.first_name} {assignment.user_detail?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', fontSize: '0.75rem' }}>
                                {assignment.shift_detail?.name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', fontSize: '0.7rem' }}>
                                {assignment.shift_detail?.start_time} - {assignment.shift_detail?.end_time}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem', textAlign: 'center' }}>
                            No active shift
                          </Typography>
                        )}
                      </Box>

                      {/* Next Shift Row */}
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: next.length > 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                          borderRadius: 1.5,
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          minHeight: '80px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: next.length > 0 ? 'flex-start' : 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontWeight: 'bold', fontSize: '0.7rem' }}>
                          ⏭️ Next
                        </Typography>
                        {next.length > 0 ? (
                          next.map((assignment, idx) => (
                            <Box key={assignment.id} sx={{ mb: idx < next.length - 1 ? 1 : 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                {assignment.user_detail?.first_name} {assignment.user_detail?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', fontSize: '0.75rem' }}>
                                {assignment.shift_detail?.name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', fontSize: '0.7rem' }}>
                                {assignment.shift_detail?.start_time} - {assignment.shift_detail?.end_time}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem', textAlign: 'center' }}>
                            No upcoming shift
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
              </Grid>  
            </Grid>  

            {/* Right column: Time-offs for today */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 1.5,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', opacity: 0.95, mb: 1.5, fontSize: '0.9rem' }}>
                  📋 Time-offs Today
                </Typography>
                {getTodayTimeOffs.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {getTodayTimeOffs.map((timeOff, idx) => (
                      <Box key={timeOff.id} sx={{ 
                        p: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: 1,
                        borderLeft: '3px solid rgba(255, 165, 0, 0.8)',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block' }}>
                          {timeOff.user_detail?.first_name} {timeOff.user_detail?.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem', display: 'block' }}>
                          {timeOff.time_off_type === 'vacation' ? '🏖️ Vacation' : '🏥 Other'}
                        </Typography>
                        {timeOff.reason && (
                          <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.65rem', display: 'block', fontStyle: 'italic' }}>
                            {timeOff.reason}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.75rem', textAlign: 'center', mt: 'auto', mb: 'auto' }}>
                    No time-offs today
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No shifts are currently scheduled for today ({moment().tz('America/New_York').format('YYYY-MM-DD')} EST).
        </Alert>
      )}

      {!canEditShifts && !isNOC && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access to shift management. Contact your administrator to request edit permissions.
        </Alert>
      )}

      {/* Selected Date Schedule Summary */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {selectedDateFormatted} Schedule
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Shift Assignments for Selected Date */}
          <Grid item xs={12}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #a0e0f6ff 0%, #5482B3 100%)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#0a2240' }}>
                  📅 Shift Assignments
                </Typography>
                {selectedDateAssignments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedDateAssignments.map(assignment => (
                      <Box
                        key={assignment.id}
                        sx={{
                          p: 2.5,
                          backgroundColor: '#fff',
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {assignment.user_detail?.first_name} {assignment.user_detail?.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.75 }}>
                            {assignment.shift_detail?.name} • {assignment.shift_detail?.start_time && assignment.shift_detail?.end_time 
                              ? `${assignment.shift_detail.start_time}-${assignment.shift_detail.end_time}` 
                              : ''} • {assignment.team_detail?.name || 'No team'}
                          </Typography>
                          {assignment.notes && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, mb: 1 }}>
                              📝 {assignment.notes}
                            </Typography>
                          )}
                          {assignment.is_on_call && (
                            <Chip label="On-Call" size="small" color="warning" sx={{ mt: 0.75 }} />
                          )}
                        </Box>
                        {canEditShifts && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenShiftDialog(assignment)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteShift(assignment.id)}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No shifts assigned for this date.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Time Offs / Vacations for 2-Week Period */}
          <Grid item xs={12}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #5482B3 0%, #0a2240 100%)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#ffffff' }}>
                  🏖️ Time-Off / Vacations ({currentWeek.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {currentWeek.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                </Typography>
                {periodTimeOffs && periodTimeOffs.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {periodTimeOffs.map(timeOff => {
                      const isOwn = timeOff.user_detail?.username === currentUser.username;
                      return (
                        <Box
                          key={timeOff.id}
                          sx={{
                            p: 1.5,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {timeOff.user_detail?.first_name} {timeOff.user_detail?.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {timeOff.time_off_type.charAt(0).toUpperCase() + timeOff.time_off_type.slice(1)} • {formatDateStringForDisplay(timeOff.start_date)} - {formatDateStringForDisplay(timeOff.end_date)}
                            </Typography>
                            {timeOff.reason && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                💬 {timeOff.reason}
                              </Typography>
                            )}
                          </Box>
                          {(isOwn || canEditShifts) && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenVacationDialog(timeOff)}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteVacation(timeOff.id)}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No time-offs scheduled for this period.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {(canEditShifts || canAddVacation) && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEditShifts && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenShiftDialog()}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add Shift Assignment for {selectedDateFormatted}
              </Button>
            )}
            {canAddVacation && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenVacationDialog()}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add Time-Off / Vacation
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Calendar View - 2 Weeks */}
      <Box sx={{ mb: 4 }}>
        <CalendarWeekView
          assignments={assignments}
          timeOffs={periodTimeOffs}
          onDateSelect={setSelectedDate}
          readonly={false}
        />
      </Box>

      {/* Dialog for adding/editing shift assignments */}
      <Dialog open={openShiftDialog} onClose={handleCloseShiftDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAssignment ? 'Edit Shift Assignment' : 'Add New Shift Assignment'}
        </DialogTitle>
        <DialogContent sx={{ pt: 6 }}>
          <TextField
            select
            fullWidth
            label="Team"
            name="team"
            value={shiftFormData.team}
            onChange={handleShiftFormChange}
            sx={{ mb: 2 }}
            required
          >
            <MenuItem value="">Select a team</MenuItem>
            {teams.map(team => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Users"
            name="users"
            value={shiftFormData.users}
            onChange={handleShiftUsersChange}
            sx={{ mb: 2 }}
            disabled={!shiftFormData.team}
            helperText={!shiftFormData.team ? "Please select a team first" : "Select one or more users"}
            SelectProps={{
              multiple: true,
            }}
          >
            {teamUsers.map(user => (
              <MenuItem key={user.id} value={user.username}>
                {user.first_name} {user.last_name} (@{user.username})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Shift"
            name="shift"
            value={shiftFormData.shift}
            onChange={handleShiftFormChange}
            sx={{ mb: 2 }}
            required
            disabled={!shiftFormData.team}
            helperText={!shiftFormData.team ? "Please select a team first" : ""}
          >
            <MenuItem value="">Select a shift</MenuItem>
            {filteredShifts.map(shift => (
              <MenuItem key={shift.id} value={shift.id}>
                {shift.name} {shift.start_time && shift.end_time ? `(${shift.start_time}-${shift.end_time})` : ''}
              </MenuItem>
            ))}
          </TextField>

          {!editingAssignment && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Apply to Dates:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                {getAvailableDates.map((dateObj) => (
                  <FormControlLabel
                    key={dateObj.dateString}
                    control={
                      <Checkbox
                        checked={shiftFormData.selectedDates.includes(dateObj.dateString)}
                        onChange={() => handleShiftDateChange(dateObj.dateString)}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {dateObj.formatted}
                        {dateObj.isToday ? ' (Today)' : ''}
                      </Typography>
                    }
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {shiftFormData.selectedDates.length} date(s) selected
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={shiftFormData.notes}
            onChange={handleShiftFormChange}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={shiftFormData.is_on_call}
                onChange={handleShiftFormChange}
                name="is_on_call"
              />
            }
            label="On-Call"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShiftDialog}>Cancel</Button>
          {!editingAssignment && (
            <Button
              onClick={() => handleShiftSave(false)}
              variant="outlined"
              disabled={!shiftFormData.shift || !shiftFormData.team || shiftFormData.users.length === 0 || shiftFormData.selectedDates.length === 0 || addLoading || updateLoading}
            >
              Add Shift and Continue
            </Button>
          )}
          <Button
            onClick={() => handleShiftSave(true)}
            variant="contained"
            disabled={!shiftFormData.shift || !shiftFormData.team || shiftFormData.users.length === 0 || (editingAssignment ? false : shiftFormData.selectedDates.length === 0) || addLoading || updateLoading}
          >
            {editingAssignment ? 'Update' : 'Add Shift and Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adding/editing vacations */}
      <Dialog open={openVacationDialog} onClose={handleCloseVacationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTimeOff ? 'Edit Time-Off' : 'Add Time-Off / Vacation'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {canEditShifts && (
            <TextField
              select
              fullWidth
              label="User"
              name="user"
              value={vacationFormData.user}
              onChange={handleVacationFormChange}
              sx={{ mb: 2, mt: 1 }}
              helperText="Select who this time-off is for (admin only)"
            >
              <MenuItem value={currentUser.username}>{currentUser.first_name} {currentUser.last_name} (You)</MenuItem>
              {adminUsers.filter(u => u.username !== currentUser.username).map(user => (
                <MenuItem key={user.id} value={user.username}>
                  {user.first_name} {user.last_name} (@{user.username})
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            type="date"
            fullWidth
            label="Start Date"
            name="start_date"
            value={vacationFormData.start_date}
            onChange={handleVacationFormChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            type="date"
            fullWidth
            label="End Date"
            name="end_date"
            value={vacationFormData.end_date}
            onChange={handleVacationFormChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            fullWidth
            label="Type"
            name="time_off_type"
            value={vacationFormData.time_off_type}
            onChange={handleVacationFormChange}
            sx={{ mb: 2 }}
          >
            {TIME_OFF_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Reason"
            name="reason"
            value={vacationFormData.reason}
            onChange={handleVacationFormChange}
            multiline
            rows={3}
            placeholder="Optional: Add a reason for your time-off request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVacationDialog}>Cancel</Button>
          <Button
            onClick={handleVacationSave}
            variant="contained"
            disabled={addVacationLoading || updateVacationLoading}
          >
            {editingTimeOff ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShiftsManagement;
